import {
  DEFAULT_OUTPUT_ID,
  useAudioDevicesStore,
} from '@/stores/audioDevicesStore'

/**
 * Routes a Web Audio graph's final output to a user-chosen audio
 * device. Preserves backward compatibility: when device switching
 * isn't available (WKWebView, older Safari, Firefox without the
 * flag), falls back to `AudioContext.destination` — the browser's
 * default sink — so nothing regresses.
 *
 * Implementation strategy:
 *   1. **AudioContext.setSinkId** — Chrome 110+ / Edge. Preferred
 *      path because it routes the entire graph without an extra
 *      MediaStream detour. We feature-detect at construction.
 *   2. **MediaStreamAudioDestinationNode + hidden `<audio>` with
 *      setSinkId** — the classic cross-browser pattern. Works in
 *      Chrome, Edge, and Safari 15.5+. Adds a small amount of
 *      latency (~10-20 ms) but is the best we can do where the
 *      direct API is missing.
 *   3. **Fallback** — connect straight to `ctx.destination`. User
 *      sees "System default (native)" in the picker with the
 *      alternative disabled.
 *
 * Lifecycle note: the sink is created once per AudioContext and
 * lives for the app's lifetime. Device changes re-target the sink
 * without recreating the graph — important because every voice is
 * already connected to the master bus's output.
 */
export class OutputSink {
  private ctx: AudioContext
  private inputNode: GainNode
  /** The source node routed to the fallback audio element (Approach B).
   *  Null when Approach A (AudioContext sinkId) is in use. */
  private streamDest: MediaStreamAudioDestinationNode | null = null
  /** Hidden `<audio>` element that actually receives the device id. */
  private audioEl: HTMLAudioElement | null = null
  /** Which approach we're using — set at construction based on
   *  feature detection. */
  private mode: 'context-sink' | 'stream-dest' | 'destination-only'
  private currentId: string = DEFAULT_OUTPUT_ID

  constructor(ctx: AudioContext) {
    this.ctx = ctx
    // Collecting point — every bus / voice connects here. We then
    // route this *once* to whichever backend is active.
    this.inputNode = ctx.createGain()
    this.inputNode.gain.value = 1

    // Feature-detect. Order matters: prefer the direct API.
    // `setSinkId` on AudioContext is documented in Chromium's
    // AudioOutputDevices spec; absence means the old runtime.
    const ctxWithSink = ctx as AudioContext & {
      setSinkId?: (id: string) => Promise<void>
    }
    const htmlAudioSupportsSinkId =
      typeof HTMLAudioElement !== 'undefined' &&
      typeof (HTMLAudioElement.prototype as unknown as { setSinkId?: unknown })
        .setSinkId === 'function'

    // Don't stomp on the native plugin's capabilities when it's in
    // play: on Catalyst the plugin reports canSwitchOutput=true with
    // outputScope='system', and we want that to win over our
    // web-side detection (which always says 'destination-only' on
    // Catalyst because WebKit doesn't expose setSinkId there).
    const store = useAudioDevicesStore.getState()
    // Snapshot the initial destination channel count so BusRack's
    // output-pair dropdown can show real options on web (where
    // enumerateDevices never exposes channel counts). We re-probe
    // after every setSinkId call so switching devices updates the
    // dropdown automatically.
    this.reportCurrentChannelCount()
    if (typeof ctxWithSink.setSinkId === 'function') {
      this.mode = 'context-sink'
      this.inputNode.connect(ctx.destination)
      store.setCanSwitchOutput(true)
      store.setOutputScope('per-session')
    } else if (
      htmlAudioSupportsSinkId &&
      typeof ctx.createMediaStreamDestination === 'function'
    ) {
      this.mode = 'stream-dest'
      this.streamDest = ctx.createMediaStreamDestination()
      this.inputNode.connect(this.streamDest)
      this.audioEl = this.createHiddenAudio(this.streamDest.stream)
      store.setCanSwitchOutput(true)
      store.setOutputScope('per-session')
    } else {
      this.mode = 'destination-only'
      this.inputNode.connect(ctx.destination)
      // Only write the "can't switch" fallback when nobody else has
      // declared a scope yet — so on Catalyst the plugin's 'system'
      // scope survives OutputSink's initialization. The plugin's
      // refresh runs asynchronously and overrides this anyway once
      // its getCapabilities resolves; guarding here avoids a flicker
      // where the UI briefly shows "picker disabled".
      if (store.outputScope === 'none') {
        store.setCanSwitchOutput(false)
      }
    }
  }

  /** Every voice / bus should end at this node. Stable for the life
   *  of the AudioContext — swapping the backing sink does not
   *  require rewiring callers. */
  get input(): AudioNode {
    return this.inputNode
  }

  /** Active output mode — surfaced so UIs can explain *why* the
   *  picker is disabled on unsupported platforms. */
  get activeMode(): 'context-sink' | 'stream-dest' | 'destination-only' {
    return this.mode
  }

  /**
   * Switch to a new output device. Accepts the raw `deviceId` from
   * `MediaDeviceInfo` or the `DEFAULT_OUTPUT_ID` sentinel to revert
   * to the browser's default sink. Rejects on failure (invalid id,
   * permission denied, device removed between selection and
   * application) — callers should surface the error to the UI.
   */
  async setDeviceId(deviceId: string): Promise<void> {
    this.currentId = deviceId
    if (this.mode === 'context-sink') {
      const ctxWithSink = this.ctx as AudioContext & {
        setSinkId: (id: string) => Promise<void>
      }
      // Chromium accepts '' or 'default' for "system default"; pass
      // an empty string when the sentinel is selected so no per-
      // device preference is stored at the OS level.
      await ctxWithSink.setSinkId(
        deviceId === DEFAULT_OUTPUT_ID ? '' : deviceId,
      )
      this.reportCurrentChannelCount()
      return
    }
    if (this.mode === 'stream-dest' && this.audioEl) {
      const audioWithSink = this.audioEl as HTMLAudioElement & {
        setSinkId: (id: string) => Promise<void>
      }
      await audioWithSink.setSinkId(
        deviceId === DEFAULT_OUTPUT_ID ? '' : deviceId,
      )
      // After setSinkId, Safari sometimes pauses the element. Re-
      // resume playback so audio continues flowing to the chosen
      // sink. A no-op on Chromium.
      if (this.audioEl.paused) void this.audioEl.play().catch(() => {})
      this.reportCurrentChannelCount()
      return
    }
    // destination-only: no switch possible. Store the selection
    // anyway so the picker reflects the user's intent for when
    // they move to a supporting browser.
    this.reportCurrentChannelCount()
  }

  /** Read `ctx.destination.maxChannelCount` and patch it onto the
   *  currently-selected output entry so BusRack's channel-pair
   *  dropdown has real options. On Chrome/Edge switching sinks
   *  updates `maxChannelCount` after `setSinkId` resolves; on
   *  Safari/Firefox it's always the default destination's count
   *  regardless of which sink is selected. */
  private reportCurrentChannelCount(): void {
    const count = this.ctx.destination.maxChannelCount
    if (!count || count < 1) return
    useAudioDevicesStore.getState().setOutputChannelCount(this.currentId, count)
  }

  get deviceId(): string {
    return this.currentId
  }

  /** Release the hidden audio element. Called if the engine is ever
   *  torn down — rare, but keeps test harnesses clean. */
  dispose(): void {
    if (this.audioEl) {
      this.audioEl.pause()
      this.audioEl.srcObject = null
      this.audioEl.remove()
      this.audioEl = null
    }
    if (this.streamDest) {
      try {
        this.inputNode.disconnect(this.streamDest)
      } catch {
        /* already disconnected */
      }
      this.streamDest = null
    }
  }

  private createHiddenAudio(stream: MediaStream): HTMLAudioElement {
    const el = document.createElement('audio')
    el.srcObject = stream
    el.autoplay = true
    el.style.display = 'none'
    el.setAttribute('aria-hidden', 'true')
    document.body.appendChild(el)
    // Autoplay policies require a user gesture on some browsers.
    // We fire-and-forget; the gesture gate is handled by the
    // existing `resume()` path in the audio engine.
    void el.play().catch(() => {
      /* ignored — user gesture will start it later */
    })
    return el
  }
}
